'use client';

import { useSearchParams } from 'next/navigation';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { FormFooter } from './components/FormFooter';
import { FormNavigation } from './components/FormNavigation';
import { FullUserInformationForm } from './components/FullUserInformation';
import { INPUT_PROMO_STAGS } from './components/PromoInput/helper';
import { RegistrationBanner } from './components/RegistrationBanner';
import { RegistrationForm } from './components/RegistrationForm';
import { useLeaveRegistration } from './hooks/useLeaveRegistration';
import { RegFormPayments } from './RegFormPayments';
import { ButtonClose } from '@/components/buttons';
import { LeaveRegistrationModal } from '@/components/dialogs/Registration/components/LeaveRegistrationModal';
import { hasShownLimitModal } from '@/components/modals/KnowYourLimit/helpers';
import { NamesSteps } from '@/helpers/auth';
import { ModalsList } from '@/helpers/breadcrumbModal';
import { getValuesFromLocalStorage } from '@/helpers/common';
import { Modals } from '@/helpers/modals';
import { useAddBreadcrumbModal } from '@/hooks/addBreadcrumbModal';
import useEstonia from '@/hooks/estonia';
import { useNavigation } from '@/hooks/navigation';
import { useIsLanding } from '@/hooks/useIsLanding';
import { sendRegFormCloseEvent } from '@/modules/googleAnalytics';
import {
  STORAGE,
  useCommonStore,
  useLocalStorageStore,
  useModalsStore,
} from '@/store';
import {
  PersistentFormName,
  usePersistentFormsSlice,
} from '@/store/commonStore/slices/persistant/usePersistentFormsSlice';

import { ScRegistration, ScRegistrationContent } from './styled';

interface IRegistrationProps {
  hideCrossClose?: boolean;
}

const Registration: FC<IRegistrationProps> = ({ hideCrossClose }) => {
  const { t } = useTranslation('common');
  const { navigate } = useNavigation();

  const searchParams = useSearchParams();
  const { isEstonia } = useEstonia();
  const limitModalShown = hasShownLimitModal();
  const {
    afterRegistrationRedirectUrl,
    afterAdditionalDataRedirectUrl,
    isAdditionalUserDataForm,
    isMobileDevice,
    isAuthenticated,
    pendingUser,
    isNavigateOnCloseRegistration,
  } = useCommonStore(
    useShallow((state) => ({
      afterRegistrationRedirectUrl: state.afterRegistrationRedirectUrl,
      afterAdditionalDataRedirectUrl: state.afterAdditionalDataRedirectUrl,
      isAdditionalUserDataForm: state.isAdditionalUserDataForm,
      isMobileDevice: state.isMobileDevice,
      isAuthenticated: state.isAuthenticated,
      pendingUser: state.pendingUser,
      isNavigateOnCloseRegistration: state.isNavigateOnCloseRegistration,
    }))
  );

  const { getCurrentPersistentStep } = usePersistentFormsSlice();
  const currentRegistrationStep = getCurrentPersistentStep(
    PersistentFormName.Registration
  ) as NamesSteps;

  const close = useModalsStore.use.close();
  const closeExact = useModalsStore.use.closeExact();

  const {
    isLeaveRegistrationOpen,
    hasShownLeaveRegistration,
    handleOpenLeaveRegistration,
    handleCloseLeaveRegistration,
  } = useLeaveRegistration({ currentRegistrationStep });

  const formRef = useRef<HTMLDivElement>(null);
  const { isLanding } = useIsLanding();

  const isPrimaryInfoStep = useMemo(
    () => currentRegistrationStep === NamesSteps.PRIMARY_INFORMATION,
    [currentRegistrationStep]
  );

  const handleShowLimitModal = useCallback(() => {
    const shouldShowLimitModal =
      isEstonia &&
      !limitModalShown &&
      ![NamesSteps.PRIMARY_INFORMATION].includes(currentRegistrationStep) &&
      !isAdditionalUserDataForm;

    if (!shouldShowLimitModal) return;

    useCommonStore.setState({ isKnowYourLimit: true });
    useLocalStorageStore
      .getState()
      .replace(STORAGE.HAS_SHOWN_LIMIT_MODAL, true);
  }, [
    currentRegistrationStep,
    isAdditionalUserDataForm,
    isEstonia,
    limitModalShown,
  ]);

  useEffect(() => {
    const redirect = searchParams.get('redirect');
    redirect &&
      useCommonStore.setState({
        afterRegistrationRedirectUrl: redirect,
        afterAdditionalDataRedirectUrl: redirect,
        afterLoginRedirectUrl: redirect,
        gameOpenBackUrl: '/',
      });

    return () => {
      useCommonStore.setState({
        afterRegistrationRedirectUrl: null,
        afterAdditionalDataRedirectUrl: null,
        afterLoginRedirectUrl: null,
      });
    };
  }, [searchParams]);

  const handleCommonRegClose = useCallback(() => {
    if (isLanding) {
      if (!isPrimaryInfoStep && afterAdditionalDataRedirectUrl) {
        return navigate(afterAdditionalDataRedirectUrl);
      }
      if (!isPrimaryInfoStep || isNavigateOnCloseRegistration) {
        return navigate('/');
      }
    } else {
      if (afterRegistrationRedirectUrl) {
        return navigate(afterRegistrationRedirectUrl);
      }
    }

    handleShowLimitModal();
    close();
  }, [
    afterAdditionalDataRedirectUrl,
    afterRegistrationRedirectUrl,
    close,
    handleShowLimitModal,
    isLanding,
    isNavigateOnCloseRegistration,
    isPrimaryInfoStep,
    navigate,
  ]);

  const closeModal = useCallback(() => {
    sendRegFormCloseEvent();

    if (!hasShownLeaveRegistration) {
      return handleOpenLeaveRegistration();
    }

    handleCommonRegClose();
  }, [
    hasShownLeaveRegistration,
    handleOpenLeaveRegistration,
    handleCommonRegClose,
  ]);

  const withStag = !!localStorage.getItem('isStag');

  const isInputPromoStag = useMemo(() => {
    if (!withStag) return;
    return INPUT_PROMO_STAGS.includes(
      getValuesFromLocalStorage('stag').split('_')[0]
    );
  }, [withStag]);

  useAddBreadcrumbModal(ModalsList.REGISTRATION);

  // this effect prevents opening of reg modal for authorized users
  useEffect(() => {
    if (!pendingUser && isAuthenticated && isPrimaryInfoStep) {
      closeExact(Modals.Registration, searchParams);
    }
  }, [
    currentRegistrationStep,
    isAuthenticated,
    pendingUser,
    searchParams,
    closeExact,
  ]);

  const onCloseRegAndLeaveReg = useCallback(() => {
    handleCloseLeaveRegistration();
    handleCommonRegClose();
  }, [handleCloseLeaveRegistration, handleCommonRegClose]);

  return (
    <>
      <ScRegistration>
        <ScRegistrationContent ref={formRef}>
          {!hideCrossClose && (
            <ButtonClose
              gray
              top={isMobileDevice ? 16 : 12}
              right={isMobileDevice ? 16 : 12}
              onClick={closeModal}
            />
          )}
          <div className="header">
            {isAdditionalUserDataForm ? (
              <>
                <h2>{t('auth.registration.userData.title')}</h2>
                <p className="description">
                  <Trans
                    i18nKey="auth.registration.userData.description"
                    components={{ b: <b /> }}
                  />
                </p>
              </>
            ) : (
              <h2>{t('auth.registration.title')}</h2>
            )}
          </div>

          {!isAdditionalUserDataForm && <RegistrationBanner />}

          {isPrimaryInfoStep ? (
            <RegistrationForm isInputPromoStag={isInputPromoStag} />
          ) : (
            <FullUserInformationForm />
          )}

          <FormNavigation currentStep={currentRegistrationStep} />
          {isPrimaryInfoStep && (
            <>
              <RegFormPayments />
              <FormFooter />
            </>
          )}
        </ScRegistrationContent>
      </ScRegistration>
      {isLeaveRegistrationOpen && (
        <LeaveRegistrationModal
          onClose={onCloseRegAndLeaveReg}
          onOpenRegistration={handleCloseLeaveRegistration}
        />
      )}
    </>
  );
};

export default Registration;
